'use server'

import { apiRequest, ApiResponse } from '../request'

export type BankAccountType = 'CHECKING' | 'SAVINGS'

export interface BankAccountItem {
    id: number
    name: string
    bankName: string
    bankCode: string | null
    agency: string | null
    accountNumber: string | null
    accountDigit?: string | null
    accountType: BankAccountType
    pixKey: string | null
    initialBalance: number
    currentBalance: number
    chartOfAccountsId: number | null
    chartOfAccounts?: { id: number; code: string; title: string } | null
    active: boolean
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateBankAccountData {
    name: string
    bankName: string
    bankCode?: string
    agency?: string
    accountNumber?: string
    /** Dígito verificador da conta — exigido pelo TED. */
    accountDigit?: string
    accountType?: BankAccountType
    pixKey?: string
    initialBalance?: number
    chartOfAccountsId?: number
}

export interface UpdateBankAccountData extends Partial<CreateBankAccountData> {
    active?: boolean
}

export interface TransferData {
    toAccountId: number
    amount: number
    date: string
    description?: string
}

export interface StatementMovement {
    id: number
    date: string
    description: string
    origin: string
    debit: number
    credit: number
    balance: number
}

export interface BankStatement {
    account: { id: number; name: string; bankName: string }
    period: { startDate: string; endDate: string }
    openingBalance: number
    closingBalance: number
    movements: StatementMovement[]
}

const BASE = 'financial/bank-accounts'

export async function getBankAccounts(): Promise<ApiResponse<BankAccountItem[]>> {
    return apiRequest<BankAccountItem[]>(BASE, {})
}

export async function getBankAccount(id: number): Promise<ApiResponse<BankAccountItem>> {
    return apiRequest<BankAccountItem>(`${BASE}/${id}`, {})
}

export async function createBankAccount(data: CreateBankAccountData): Promise<ApiResponse<BankAccountItem>> {
    return apiRequest<BankAccountItem>(BASE, { data }, 'post')
}

export async function updateBankAccount(id: number, data: UpdateBankAccountData): Promise<ApiResponse<BankAccountItem>> {
    return apiRequest<BankAccountItem>(`${BASE}/${id}`, { data }, 'patch')
}

export async function deleteBankAccount(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${id}`, {}, 'delete')
}

export async function transferBetweenAccounts(fromAccountId: number, data: TransferData): Promise<ApiResponse<any>> {
    return apiRequest<any>(`${BASE}/${fromAccountId}/transfer`, { data }, 'post')
}

export async function recalculateBalance(id: number): Promise<ApiResponse<BankAccountItem>> {
    return apiRequest<BankAccountItem>(`${BASE}/${id}/recalculate-balance`, {}, 'post')
}

export async function getBankAccountStatement(
    id: number,
    startDate: string,
    endDate: string,
): Promise<ApiResponse<BankStatement>> {
    return apiRequest<BankStatement>(`${BASE}/${id}/statement`, { params: { startDate, endDate } })
}
